import React, { useState, useEffect } from 'react'
import { Form } from 'formik'
import { useTranslation, Trans } from 'react-i18next'
import styled from 'styled-components'
import { Button } from '@/components/button'
import { FieldDropdown } from '@/components/form'
import { CatalogProduct } from '@/features/Payments/types'
import { CreditCardInput } from '@/features/Payments/forms'
import { StateDropdown } from '@/components/address-form'
import { ShieldIcon } from '@/components/icons'
import useBasic from '@/hooks/use-basic'
import useMediaQuery from '@/hooks/use-media-query'
import { getFormattedCountries } from '@/utils/address'
import { Row, Group, FieldInput } from '@/components/form'
import { SwitchToggle } from '@/components/form'
import { DeleteIcon } from '@/components/icons'
import Skeleton from '@/components/skeleton'
import { fontWeights, fontSizes, colors, breakpoints } from '@/styles'

type FormValues = {
  payment_method: 'stripe'
  default_payment_method: boolean
  first_name: string
  last_name: string
  street1: string
  street2?: string
  city: string
  state: string
  country: string
  zip: string
  card_number: string
  expiration_date: string
  security_code: string
  save_payment_method: boolean
  save_billing_address: boolean
  tax_code?: string
}

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 12px;

  .css-zi2v5r-menu {
    max-height: 200px;

    > :nth-child(n) {
      max-height: 200px;
    }
  }
`
const SectionTitle = styled.div`
  font-weight: ${fontWeights.medium};
  font-size: ${fontSizes.medium};
`
const SavePaymentMethodWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    padding: 0 32px;
  }
`
const Secure = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${fontSizes.small};

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    width: 100%;
    justify-content: center;
  }
`
const ActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
`
const Terms = styled.div`
  font-size: ${fontSizes.small};
  overflow-wrap: break-word;
  margin-top: 30px;
`
const SkeletonTerm = styled(Terms)`
  display: flex;
  flex-direction: column;
  gap: 5px;
`
const Link = styled.a`
  color: ${colors.link};
  text-decoration: underline;
`
const MobileSummary = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${colors.grey96};
  padding: 32px;
  margin-top: 30px;
  gap: 24px;
`
const Summary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  > :last-child {
    border-top: 2px solid ${colors.grey88};
    padding-top: 12px;
  }
`
const Total = styled.div`
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.medium};
`
const StyledRow = styled(Row)`
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    padding: 0 32px;
    width: 100%;
    display: flex;
    justify-content: space-between;
  }
`
const SummaryRow = styled(Row)`
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    display: flex;
    justify-content: space-between;
    margin: 0;
  }
`
const Section = styled.div`
  display: flex;
  align-items: center;
  padding: 24px 0;
  justify-content: space-between;
`

type FormProps = {
  values: FormValues
  isValid: boolean
  setFieldValue: (field: string, value: any) => void
  card?: {
    brand: string
    card_last4: string
    card_number: string
    city: string
    country: string
    expiration_date: string
    first_name: string
    is_default: boolean
    last_name: string
    model_name: null
    payment_method_id: string
    state: string
    street1: string
    street2: string | null
    tax_code: string | null
    zip: string
  }
  onSubmit?: () => void
  setSavePaymentMethod?: React.Dispatch<React.SetStateAction<boolean>>
  savePaymentMethod?: boolean
  product?: CatalogProduct
  edit?: boolean
  onDelete?: any
  loading?: boolean
  deleting?: boolean
  showSkeleton?: boolean
  setState?: any
  totalPrice?: any
}

const PaymentForm: React.FC<FormProps> = ({
  values,
  isValid,
  setFieldValue,
  onSubmit,
  card,
  setSavePaymentMethod,
  savePaymentMethod,
  product,
  edit,
  onDelete,
  loading,
  deleting,
  showSkeleton = false,
  setState,
  totalPrice
}) => {
  const { t } = useTranslation()
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const colSize = displayMobile ? 12 : 6
  const { countries } = useBasic(['countries'])
  const formattedCountries = getFormattedCountries(countries)

  const [cardInfoValid, setCardInfoValid] = useState(false)
  const [cardMethodValid, setCardMethodValid] = useState(false)

  const [open, setOpen] = useState(!!values.tax_code)
  const submitting = loading || !cardInfoValid || !cardMethodValid || !isValid || deleting

  const handleStateChange = (state: string) => {
    if (state !== values.state) {
      setOpen(false)
      setFieldValue('tax_code', '')
    }
  }

  useEffect(() => {
    if (!edit) {
      setState(values.state)
    }
  }, [values.state])

  return showSkeleton ? (
    <StyledForm>
      <StyledRow>
        <Group col={colSize}>
          <Skeleton height={50} />
        </Group>
        <Group col={colSize}>
          <Skeleton height={50} />
        </Group>
      </StyledRow>

      <StyledRow>
        <Group col={12}>
          <Skeleton height={50} />
        </Group>
      </StyledRow>

      <StyledRow top={16}>
        <Group col={12}>
          <SectionTitle>
            <Skeleton height={25} width={200} />
          </SectionTitle>
        </Group>
      </StyledRow>
      <StyledRow>
        <Group col={colSize}>
          <Skeleton height={50} />
        </Group>
        <Group col={colSize}>
          <Skeleton height={50} />
        </Group>
      </StyledRow>
      <StyledRow>
        <Group col={colSize}>
          <Skeleton height={50} />
        </Group>
        <Group col={colSize}>
          <Skeleton height={50} />
        </Group>
      </StyledRow>
      <StyledRow>
        <Group col={colSize}>
          <Skeleton height={50} />
        </Group>
        <Group col={colSize}>
          <Skeleton height={50} />
        </Group>
      </StyledRow>

      {setSavePaymentMethod && (
        <SavePaymentMethodWrapper>
          <Skeleton height={25} width={200} />
        </SavePaymentMethodWrapper>
      )}

      {displayMobile && !edit ? (
        <MobileSummary>
          <Summary>
            <SummaryRow>
              <div>{`1 x ${product?.name}`}</div>
              <div>
                {showSkeleton ? <Skeleton width={50} height={10} /> : `$ ${product?.value}`}
              </div>
            </SummaryRow>

            <SummaryRow>
              <div>{`GST/HST`}</div>
              <div>
                {showSkeleton ? <Skeleton width={50} height={10} /> : `$ ${product?.fees[0].value}`}
              </div>
            </SummaryRow>

            <SummaryRow>
              <Total>{`Total`}</Total>
              <Total>
                {showSkeleton ? <Skeleton width={50} height={10} /> : `$ ${product?.total_due}`}
              </Total>
            </SummaryRow>
          </Summary>

          <Button onClick={onSubmit} disabled id="application-trust-score-submit-purchase">
            {t('l.trust_score.blockpay.submit_purchase.text', 'Submit Purchase')}
          </Button>

          <Secure>
            <ShieldIcon width={14} height={14} />
            <span>{t('l.trust_score.blockpay.secure_encryption.text', 'Secure Encryption')}</span>
          </Secure>
        </MobileSummary>
      ) : (
        <>
          <SkeletonTerm>
            <Skeleton height={15} />
            <Skeleton height={15} />
            <Skeleton height={15} width={100} />
          </SkeletonTerm>

          {edit ? (
            <Section>
              <Button
                theme={'delete'}
                onClick={onDelete}
                disabled={loading || deleting}
                loading={deleting}
              >
                <DeleteIcon />
                Delete
              </Button>
              <Button onClick={onSubmit} loading={loading} disabled={submitting}>
                Save
              </Button>
            </Section>
          ) : (
            <ActionRow>
              <Secure>
                <ShieldIcon width={14} height={14} />
                <span>
                  {t('l.trust_score.blockpay.secure_encryption.text', 'Secure Encryption')}
                </span>
              </Secure>

              <Button
                id="application-trust-score-submit-purchase"
                onClick={onSubmit}
                loading={loading}
                disabled={submitting}
              >
                {t('l.trust_score.blockpay.submit_purchase.text', 'Submit Purchase')}
              </Button>
            </ActionRow>
          )}
        </>
      )}
    </StyledForm>
  ) : (
    <StyledForm>
      <StyledRow>
        <Group col={colSize}>
          <FieldInput
            id="first_name"
            name="first_name"
            value={values.first_name}
            autoComplete={' billing cc-given-name'}
            placeholder={t('l.plan_settings.edit.first_name.label', 'First Name')}
            required
          />
        </Group>
        <Group col={colSize}>
          <FieldInput
            id="last_name"
            name="last_name"
            value={values.last_name}
            autoComplete={'billing cc-family-name'}
            placeholder={t('l.plan_settings.edit.last_name.label', 'Last Name')}
            required
          />
        </Group>
      </StyledRow>

      <StyledRow>
        <Group col={12}>
          <CreditCardInput
            autocompletePrefix="billing"
            setCardInfoValid={setCardInfoValid}
            setCardMethodValid={setCardMethodValid}
            placeholder={card?.card_number}
            cardMethods={{
              acceptedCards: ['unionpay', 'visa', 'mastercard', 'american-express'],
              error: t(
                'r.payment.edit.unknown_card.error',
                '** The credit card you have entered is not accepted. please try again with a different card.'
              )
            }}
          />
        </Group>
      </StyledRow>

      <StyledRow top={16}>
        <Group col={12}>
          <SectionTitle>
            {t('l.listing.edit.advertise_listing.billing_address.title', 'Billing Address')}
          </SectionTitle>
        </Group>
      </StyledRow>
      <StyledRow>
        <Group col={colSize}>
          <FieldInput
            name="street1"
            value={values.street1}
            autoComplete={'billing address-line1'}
            placeholder={t('l.plan_settings.edit.street_address.label', 'Street address')}
            required
          />
        </Group>
        <Group col={colSize}>
          <FieldInput
            name="street2"
            value={values.street2 || ''}
            placeholder={t('l.plan_settings.edit.street_address_2.label', 'Suite no. / Unit no.')}
          />
        </Group>
      </StyledRow>
      <StyledRow>
        <Group col={colSize}>
          <FieldInput
            name="city"
            value={values.city}
            autoComplete={'billing address-level2'}
            placeholder={t('l.plan_settings.edit.city.label', 'City')}
            required
          />
        </Group>
        <Group col={colSize}>
          <StateDropdown
            name="state"
            country={values.country}
            isSearchable={false}
            required
            menuPlacement={displayMobile ? 'top' : undefined}
            autoComplete={'billing address-level1'}
            onChange={handleStateChange}
          />
        </Group>
      </StyledRow>
      <StyledRow>
        <Group col={colSize}>
          <FieldDropdown
            id="country"
            name="country"
            options={formattedCountries}
            autoComplete="billing country"
            onChange={() => setFieldValue('state', '')}
            placeholder={t('r.settings.edit.country.label', 'Country')}
            required
            isSearchable={false}
            isDisabled
          />
        </Group>
        <Group col={colSize}>
          <FieldInput
            name="zip"
            value={values.zip}
            autoComplete="billing postal-code"
            placeholder={t('r.payment.edit.make_payment_zip.label', 'Postal code')}
            required
          />
        </Group>
      </StyledRow>

      {setSavePaymentMethod && (
        <SavePaymentMethodWrapper>
          <SwitchToggle
            id="save_payment_method"
            onChange={() => setSavePaymentMethod(!savePaymentMethod)}
          />
          <label htmlFor="save_payment_method">
            {t('l.trust_score.blockpay.save_payment_method.text', 'Save payment method')}
          </label>
        </SavePaymentMethodWrapper>
      )}

      {displayMobile && !edit ? (
        <MobileSummary>
          <Summary>
            <SummaryRow>
              <div>{`1 x ${product?.name}`}</div>
              <div>{`$ ${product?.value}`}</div>
            </SummaryRow>

            <SummaryRow>
              <div>{`GST/HST`}</div>
              <div>{`$ ${product?.fees[0].value}`}</div>
            </SummaryRow>

            <SummaryRow>
              <Total>{`Total`}</Total>
              <Total>{`$ ${product?.total_due}`}</Total>
            </SummaryRow>
          </Summary>

          <Button onClick={onSubmit} loading={loading} disabled={submitting}>
            {t('l.trust_score.blockpay.submit_purchase.text', 'Submit Purchase')}
          </Button>

          <Secure>
            <ShieldIcon width={14} height={14} />
            <span>{t('l.trust_score.blockpay.secure_encryption.text', 'Secure Encryption')}</span>
          </Secure>
        </MobileSummary>
      ) : (
        <>
          <Terms>
            <Trans
              i18nKey="l.trust_score.blockpay.purchase_terms_no_method.text"
              values={{ totalPrice: product?.total_due }}
              t={t}
            >
              By clicking “Submit Purchase”, I agree to a one-time charge of ${{ totalPrice }} for
              this purchase and I confirm that I have read and agree to liv.rent's{' '}
              <Link href="https://liv.rent/privacy" target="_blank" rel="noreferrer">
                Privacy Policy
              </Link>{' '}
              and{' '}
              <Link href="https://liv.rent/terms" target="_blank" rel="noreferrer">
                Terms & Conditions
              </Link>
              . liv.rent bears no responsibility for results returned from Equifax®.
            </Trans>
          </Terms>

          {edit ? (
            <Section>
              <Button
                theme={'delete'}
                onClick={onDelete}
                disabled={loading || deleting}
                loading={deleting}
              >
                <DeleteIcon />
                Delete
              </Button>
              <Button onClick={onSubmit} loading={loading} disabled={submitting}>
                Save
              </Button>
            </Section>
          ) : (
            <ActionRow>
              <Secure>
                <ShieldIcon width={14} height={14} />
                <span>
                  {t('l.trust_score.blockpay.secure_encryption.text', 'Secure Encryption')}
                </span>
              </Secure>

              <Button
                id="application-trust-score-submit-purchase"
                onClick={onSubmit}
                loading={loading}
                disabled={submitting}
              >
                {t('l.trust_score.blockpay.submit_purchase.text', 'Submit Purchase')}
              </Button>
            </ActionRow>
          )}
        </>
      )}
    </StyledForm>
  )
}

export default PaymentForm
